import { useMemo } from 'react';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { removeDspClicks } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { onDisplayAdvertisingMode } from 'src/store/modules/user/selectors';
import useGenericAdvancedSearch, { UseGenericAdvancedSearchParameters } from 'src/utils/Hooks/useGenericAdvancedSearch';
import { calculatePercentChange, getSearchAppName } from 'src/utils/app';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import AdvancedSearchRequestBuilder, {
  useAdvancedSearchRequestBuilder
} from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { ValueOf } from 'sl-api-connector';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { SplineChartV2DataPoint, SplineChartV2Series } from './SplineChartV2';
import { calculateMetricTotal, calculateComparisonTotal } from './metricTotalUtils';
import { useComparisonWeekIds } from './useComparisonWeekIds';
import { alignDataToWeekIds, AlignDataToWeekIdsOptions } from './splineChartV2Utils';
import { getWeekIdRange } from 'src/utils/dateUtils';
import { useAreCategoriesLoaded, useIsCurrentSegmentLoaded } from 'src/utils/Hooks/reduxSelectors';

/**
 * Arguments for useMetricByWeekIdV2 hook
 */
export interface UseMetricByWeekIdV2Args {
  /** The index name to query (e.g., 'sales', 'traffic') */
  indexName: string;
  /** The field name to query (e.g., 'retailSales', 'unitsSold') */
  fieldName: string;
  /** Optional different index for comparison data */
  comparisonIndexName?: string;
  /** Optional different field for comparison data */
  comparisonFieldName?: string;
  /** Entity type for field lookup */
  entityType?: string;
  /**
   * Allows optionally modifying the primary aggregation builder
   * before the request is fired
   */
  primaryAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;
  /**
   * Allows optionally modifying the secondary aggregation builder
   * before the request is fired
   */
  secondaryAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;
  /**
   * Allows optionally modifying the request builder before the request is fired
   */
  requestBuilder?: (requestBuilder: AdvancedSearchRequestBuilder, isComparison: boolean) => AdvancedSearchRequestBuilder;
  /**
   * Override metric type for formatting
   */
  metricTypeOverride?: ValueOf<typeof METRICTYPE>;
  /**
   * Additional args for useGenericAdvancedSearch
   */
  useGenericAdvancedSearchArgs?: Partial<UseGenericAdvancedSearchParameters>;
  /**
   * Skip data alignment to Redux mainTimePeriod week IDs.
   * When true, returns raw data as-is from the API without padding for weeks
   * that weren't returned. Useful for forecasts where the actual week range
   * differs from Redux mainTimePeriod.
   */
  skipDataAlignment?: boolean;
  /**
   * When true, missing weeks are padded with 0; when false, with null.
   * @default true — we pad with 0 (product decision) so the chart line connects across the full range.
   */
  fillNullWithZero?: boolean;
  /**
   * Override how the legend total is computed from the series: 'sum' | 'avg' | 'cardinality' etc.
   * When provided, takes precedence over the field's timePeriodAggregationFunctionType
   * (e.g. use sum even when the field defaults to lastValue).
   */
  aggregationFunctionForTotal?: string;
  /**
   * When true, comparison data uses the same time period as primary (e.g., compare mode
   * comparing different metrics or groups). In this case, comparison data should be
   * aligned to mainPeriodWeekIds instead of comparisonWeekIdsForMainPeriod.
   */
  isComparisonSameTimePeriod?: boolean;
  /**
   * When provided, enables market share mode. This builder modifies a clone of the
   * brand request to remove brand-specific filters, producing a "total market" request.
   * Market share is then computed as (brand value / market total value) per week.
   */
  marketShareBuilder?: (requestBuilder: AdvancedSearchRequestBuilder) => AdvancedSearchRequestBuilder;
}

/**
 * Response type from the Advanced Search API
 */
interface AdvancedSearchByWeekIdResponse<TAdditionalValues> {
  aggregations: {
    by_weekId: {
      fieldId: string;
      additionalValues: TAdditionalValues;
    }[];
  };
}

/**
 * Raw row from the API with weekId and metric values
 */
interface MetricRow {
  weekId: number;
  [key: string]: number;
}

/**
 * Helper function to get a field by index name and field name
 */
const getField = (indexName: string, fieldName: string, entityType?: string) => {
  return INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType);
};

/**
 * Get the key from the response based on field configuration
 */
const getMetricKey = (indexName: string, fieldName: string, entityType?: string) => {
  const field = getField(indexName, fieldName, entityType);
  const keyEnding = field.aggregationFunction === 'stats' ? 'avg' : 'value';
  return `${field.name}_${field.aggregationFunction}_${keyEnding}`;
};

const toSplineChartV2Data = (
  rows: MetricRow[],
  indexName: string,
  fieldName: string,
  entityType?: string
): SplineChartV2DataPoint[] => {
  if (!rows?.length) return [];

  const metricKey = getMetricKey(indexName, fieldName, entityType);

  return rows
    .sort((a, b) => a.weekId - b.weekId)
    .map((row) => ({
      weekId: row.weekId,
      value: row[metricKey] ?? null
    }));
};

/**
 * Computes market share per week: (brand value / market total value).
 * Both brand and market rows are keyed by weekId.
 */
const toMarketShareData = (
  brandRows: MetricRow[],
  marketRows: MetricRow[],
  indexName: string,
  fieldName: string,
  entityType?: string
): SplineChartV2DataPoint[] => {
  if (!brandRows?.length || !marketRows?.length) return [];

  const metricKey = getMetricKey(indexName, fieldName, entityType);
  const marketByWeekId = new Map<number, number>();
  for (const row of marketRows) {
    const val = row[metricKey];
    if (val != null) marketByWeekId.set(row.weekId, val);
  }

  return brandRows
    .sort((a, b) => a.weekId - b.weekId)
    .map((row) => {
      const brandVal = row[metricKey] ?? 0;
      const marketVal = marketByWeekId.get(row.weekId);
      if (!marketVal || marketVal === 0) return { weekId: row.weekId, value: null };
      return { weekId: row.weekId, value: brandVal / marketVal };
    });
};

/**
 * Computes overall market share: sum(brand values) / sum(market values) across all weeks.
 */
const computeMarketShareTotal = (
  brandRows: MetricRow[],
  marketRows: MetricRow[],
  indexName: string,
  fieldName: string,
  entityType?: string
): number => {
  const metricKey = getMetricKey(indexName, fieldName, entityType);
  let brandTotal = 0;
  let marketTotal = 0;

  for (const row of brandRows) {
    brandTotal += row[metricKey] ?? 0;
  }
  for (const row of marketRows) {
    marketTotal += row[metricKey] ?? 0;
  }

  return marketTotal !== 0 ? brandTotal / marketTotal : 0;
};

/**
 * Return type for the useMetricByWeekIdV2 hook
 */
export interface UseMetricByWeekIdV2Result {
  // ============ Series Data (ready for SplineChartV2) ============
  /** Primary series data ready for SplineChartV2 */
  primarySeries: SplineChartV2Series;
  /** Comparison series data ready for SplineChartV2 (shifted to current year weekIds if applicable) */
  comparisonSeries: SplineChartV2Series;

  // ============ Raw Data Points ============
  /** Raw primary data points */
  primaryData: SplineChartV2DataPoint[];
  /** Raw comparison data points */
  comparisonData: SplineChartV2DataPoint[];

  // ============ Loading State ============
  /** Whether the data is loading */
  isLoading: boolean;

  // ============ Field Info ============
  /** The metric type for this field */
  metricType: ValueOf<typeof METRICTYPE>;
  /** The field configuration from INDEX_FIELDS */
  field: ReturnType<typeof getField>;

  // ============ Raw Numeric Values ============
  /** Total value for primary data */
  primaryTotal: number;
  /** Total value for comparison data */
  comparisonTotal: number;
  /** Percent change between primary and comparison totals */
  percentChange: number | undefined;
  /** Last primary value */
  lastPrimaryValue: number | null;
  /** Last comparison value */
  lastComparisonValue: number | null;
  /** Percent change between last values */
  lastValuePercentChange: number | undefined;

  // ============ Formatted Values (display-ready strings) ============
  /** Formatted primary total (e.g., "$1.2M", "45.2%") */
  formattedPrimaryTotal: string;
  /** Formatted comparison total */
  formattedComparisonTotal: string;
  /** Formatted percent change (e.g., "12.5%") */
  formattedPercentChange: string;
  /** Formatted last primary value */
  formattedLastPrimaryValue: string;
  /** Formatted last comparison value */
  formattedLastComparisonValue: string;
  /** Formatted percent change between last values */
  formattedLastValuePercentChange: string;

  // ============ Time Period Info ============
  /** Whether this is a YTD with Prior Year comparison (comparison period is expanded to full year) */
  isYTDWithPriorYear: boolean;
  /** Whether this is Last Week with Prior Year comparison (main period is expanded to YTD) */
  isLastWeekWithPriorYear: boolean;
  /** The start weekId for the primary period */
  primaryStartWeekId: number;
  /** The end weekId for the primary period */
  primaryEndWeekId: number;
  /** The start weekId for the comparison period (may be expanded for YTD + Prior Year) */
  comparisonStartWeekId: number;
  /** The end weekId for the comparison period (may be expanded for YTD + Prior Year) */
  comparisonEndWeekId: number;

  // ============ Raw Response ============
  /** Raw API response for custom calculations (e.g., weighted averages) */
  response: unknown;
}

/**
 * A simplified hook for fetching metric data by weekId that returns
 * data in SplineChartV2 format with minimal transformations.
 *
 * Key differences from useMetricByWeekId:
 * - Returns data as SplineChartV2DataPoint[] (simple { weekId, value } objects)
 * - Returns pre-built SplineChartV2Series objects ready to pass to SplineChartV2
 * - Handles prior year comparison shifting internally
 * - Simpler return structure focused on what SplineChartV2 needs
 */
export const useMetricByWeekIdV2 = ({
  indexName,
  fieldName,
  comparisonFieldName,
  comparisonIndexName,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  requestBuilder,
  metricTypeOverride,
  useGenericAdvancedSearchArgs = {},
  skipDataAlignment = false,
  fillNullWithZero,
  aggregationFunctionForTotal,
  isComparisonSameTimePeriod = false,
  marketShareBuilder,
}: UseMetricByWeekIdV2Args): UseMetricByWeekIdV2Result => {
  // Pad missing weeks with 0 by default (product decision: smooth line across full range).
  const shouldFillNullWithZero = fillNullWithZero ?? true;
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters(indexName);
  const formatMetric = useMetricFormatter();
  const isAdvertisingDisplayMode = useAppSelector(onDisplayAdvertisingMode);

  // Check if categories are loaded to prevent race condition during retailer switch
  const areCategoriesLoaded = useAreCategoriesLoaded();

  // Check if current segment is loaded (for segment entity types)
  const isCurrentSegmentLoaded = useIsCurrentSegmentLoaded();

  // Use the new hook that fetches comparison week IDs from the microservice API
  // This properly handles 52/53 week year mappings
  const {
    startWeekId,
    endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    isLoadingComparisonWeeks,
    isYTDWithPriorYear,
    isLastWeekWithPriorYear,
    comparisonWeekIdsForMainPeriod
  } = useComparisonWeekIds();

  const entity = useAppSelector((state) => state?.entityService?.mainEntity);
  const requestIdWithEntityInfo = `useMetricByWeekIdV2-${indexName}-${fieldName}-${entity?.type}-${entity?.id}`;
  const retailerId = useAppSelector((state) => state.retailer.id);

  // Field configuration
  // Pass 'weekId' as groupByFieldName to apply weekId_Override (e.g., for cardinality aggregation)
  const field = useMemo(
    () => INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType, 'weekId'),
    [indexName, fieldName, entityType]
  );

  // Request builder base
  const baseRequestBuilderFromHook = useAdvancedSearchRequestBuilder(
    requestIdWithEntityInfo,
    `${getSearchAppName()}-${indexName}`
  );

  const baseRequestBuilder = useMemo(() => {
    let build = baseRequestBuilderFromHook
      .setDoAggregation(true)
      .setPageNumber(1)
      .setPageSize(1200)
      .setPeriod('year')
      .setReturnDocuments(false)
      .setSearchBy('parent');

    if (!isAdvertisingDisplayMode) {
      build = removeDspClicks(build);
    }

    return build;
  }, [baseRequestBuilderFromHook, isAdvertisingDisplayMode]);

  // Aggregation builder base
  const baseAggregationBuilder = useMemo(() => {
    return new AggregationBuilder('weekId').addConditionTermFilter('retailerId', [retailerId]);
  }, [retailerId]);

  // Main aggregation builder
  const mainAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction as string,
        true,
        field.aggregationFunctionExpression
      )
      .addConditionRangeFilter('weekId', startWeekId, endWeekId);

    if (!isAdvertisingDisplayMode) {
      aggBuilder.addConditionTermFilter('searchTerm', ['[DSP Clicks]'], 'must_not');
    }

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction as string,
          true
        );
      });
    }

    return primaryAggregationBuilder ? primaryAggregationBuilder(aggBuilder.clone()) : aggBuilder;
  }, [
    baseAggregationBuilder,
    field,
    startWeekId,
    endWeekId,
    isAdvertisingDisplayMode,
    primaryAggregationBuilder
  ]);

  // Main request body
  const mainRequestBody = useMemo(() => {
    const body = baseRequestBuilder
      .clone()
      .addAggregation(mainAggregationBuilder.build())
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters)
      .addConditionRangeFilter('weekId', startWeekId, endWeekId);

    return requestBuilder ? requestBuilder(body, false).build() : body.build();
  }, [
    baseRequestBuilder,
    mainAggregationBuilder,
    applyEntityFilters,
    removeRetailPriceRangeFilters,
    startWeekId,
    endWeekId,
    requestBuilder
  ]);

  // Comparison aggregation builder
  const comparisonAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction as string,
        true,
        field.aggregationFunctionExpression
      )
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction as string,
          true
        );
      });
    }

    return secondaryAggregationBuilder ? secondaryAggregationBuilder(aggBuilder.clone()) : aggBuilder;
  }, [
    baseAggregationBuilder,
    field,
    comparisonStartWeekId,
    comparisonEndWeekId,
    secondaryAggregationBuilder
  ]);

  // Comparison request body
  const comparisonRequestBody = useMemo(() => {
    const body = baseRequestBuilder
      .clone()
      .addAggregation(comparisonAggregationBuilder.build())
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters)
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);

    if (!isAdvertisingDisplayMode) {
      body.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Clicks]']);
    }

    return requestBuilder ? requestBuilder(body, true).build() : body.build();
  }, [
    baseRequestBuilder,
    comparisonAggregationBuilder,
    applyEntityFilters,
    removeRetailPriceRangeFilters,
    comparisonStartWeekId,
    comparisonEndWeekId,
    isAdvertisingDisplayMode,
    requestBuilder
  ]);

  // Market total request bodies (for market share mode)
  const marketMainRequestBody = useMemo(() => {
    if (!marketShareBuilder) return null;
    const body = baseRequestBuilder
      .clone()
      .addAggregation(mainAggregationBuilder.build())
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters)
      .addConditionRangeFilter('weekId', startWeekId, endWeekId);

    const withRequestBuilder = requestBuilder ? requestBuilder(body, false) : body;
    return marketShareBuilder(withRequestBuilder).build();
  }, [
    marketShareBuilder,
    baseRequestBuilder,
    mainAggregationBuilder,
    applyEntityFilters,
    removeRetailPriceRangeFilters,
    startWeekId,
    endWeekId,
    requestBuilder
  ]);

  const marketComparisonRequestBody = useMemo(() => {
    if (!marketShareBuilder) return null;
    const body = baseRequestBuilder
      .clone()
      .addAggregation(comparisonAggregationBuilder.build())
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters)
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);

    if (!isAdvertisingDisplayMode) {
      body.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Clicks]']);
    }

    const withRequestBuilder = requestBuilder ? requestBuilder(body, true) : body;
    return marketShareBuilder(withRequestBuilder).build();
  }, [
    marketShareBuilder,
    baseRequestBuilder,
    comparisonAggregationBuilder,
    applyEntityFilters,
    removeRetailPriceRangeFilters,
    comparisonStartWeekId,
    comparisonEndWeekId,
    isAdvertisingDisplayMode,
    requestBuilder
  ]);

  const allRequestBodies = useMemo(() => {
    const bodies = [mainRequestBody, comparisonRequestBody];
    if (marketMainRequestBody) bodies.push(marketMainRequestBody);
    if (marketComparisonRequestBody) bodies.push(marketComparisonRequestBody);
    return bodies;
  }, [mainRequestBody, comparisonRequestBody, marketMainRequestBody, marketComparisonRequestBody]);

  // Fetch data - skip fetch until categories and segment (if applicable) are loaded
  const isReadyToFetch = areCategoriesLoaded && isCurrentSegmentLoaded;
  const { data: response, isLoading: isLoadingData } = useGenericAdvancedSearch<AdvancedSearchByWeekIdResponse<any>[]>({
    requestBody: allRequestBodies,
    requestId: requestIdWithEntityInfo,
    queryKeys: allRequestBodies,
    shouldPerformFetch: isReadyToFetch,
    ...useGenericAdvancedSearchArgs
  });

  // Combined loading state (data + comparison week IDs from API + categories/segment loading)
  const isLoading = isLoadingData || isLoadingComparisonWeeks || !isReadyToFetch;

  // Parse main rows
  const mainRows: MetricRow[] = useMemo(() => {
    if (!response?.data?.[0]?.aggregations?.by_weekId) return [];
    return response.data[0].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
      weekId: Number(fieldId),
      ...additionalValues
    }));
  }, [response]);

  // Parse comparison rows
  const comparisonRows: MetricRow[] = useMemo(() => {
    if (!response?.data?.[1]?.aggregations?.by_weekId) return [];
    return response.data[1].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
      weekId: Number(fieldId),
      ...additionalValues
    }));
  }, [response]);

  // Parse market total rows (for market share mode, indices 2 and 3)
  const marketMainRows: MetricRow[] = useMemo(() => {
    if (!marketShareBuilder || !response?.data?.[2]?.aggregations?.by_weekId) return [];
    return response.data[2].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
      weekId: Number(fieldId),
      ...additionalValues
    }));
  }, [response, marketShareBuilder]);

  const marketComparisonRows: MetricRow[] = useMemo(() => {
    if (!marketShareBuilder || !response?.data?.[3]?.aggregations?.by_weekId) return [];
    return response.data[3].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
      weekId: Number(fieldId),
      ...additionalValues
    }));
  }, [response, marketShareBuilder]);

  // Generate expected week IDs for the main period
  const mainPeriodWeekIds = useMemo(
    () => getWeekIdRange(startWeekId, endWeekId),
    [startWeekId, endWeekId]
  );

  // Generate full comparison period week IDs (for YTD + Prior Year, this spans the full year)
  const fullComparisonPeriodWeekIds = useMemo(
    () => getWeekIdRange(comparisonStartWeekId, comparisonEndWeekId),
    [comparisonStartWeekId, comparisonEndWeekId]
  );

  // Convert to SplineChartV2 data format.
  // In market share mode, compute ratio per week: (brand value / market total value)
  const rawPrimaryData = useMemo(() => {
    if (marketShareBuilder && marketMainRows.length > 0) {
      return toMarketShareData(mainRows, marketMainRows, indexName, fieldName, entityType);
    }
    return toSplineChartV2Data(mainRows, indexName, fieldName, entityType);
  }, [mainRows, marketMainRows, marketShareBuilder, indexName, fieldName, entityType]);

  const rawComparisonData = useMemo(() => {
    const compIdx = comparisonIndexName || indexName;
    const compField = comparisonFieldName || fieldName;
    if (marketShareBuilder && marketComparisonRows.length > 0) {
      return toMarketShareData(comparisonRows, marketComparisonRows, compIdx, compField, entityType);
    }
    return toSplineChartV2Data(comparisonRows, compIdx, compField, entityType);
  }, [comparisonRows, marketComparisonRows, marketShareBuilder, comparisonIndexName, indexName, comparisonFieldName, fieldName, entityType]);

  // For YTD + Prior Year or Last Week + Prior Year: chart should span the full year
  // - Primary data: align to main period week IDs (YTD/expanded portion)
  // - Comparison data: align to full comparison period (entire prior year)
  // For other scenarios: both series should align to their respective periods,
  // with comparison aligned to comparisonWeekIdsForMainPeriod for proper 1:1 mapping
  // Note: Last Week + Prior Year expands to YTD internally (see useComparisonWeekIds),
  // but comparison should still show the full prior year.
  const shouldSpanFullYear = isYTDWithPriorYear || isLastWeekWithPriorYear;

  // Alignment options: missing weeks padded with 0 by default (product decision)
  const alignmentOptions: AlignDataToWeekIdsOptions = useMemo(
    () => ({ fillNullWithZero: shouldFillNullWithZero }),
    [shouldFillNullWithZero]
  );

  const primaryData = useMemo(() => {
    // Skip alignment for forecasts or when explicitly disabled
    if (skipDataAlignment) return rawPrimaryData;
    if (!mainPeriodWeekIds.length) return rawPrimaryData;
    return alignDataToWeekIds(rawPrimaryData, mainPeriodWeekIds, alignmentOptions);
  }, [rawPrimaryData, mainPeriodWeekIds, alignmentOptions, skipDataAlignment]);

  // For comparison data:
  // - Compare mode (isComparisonSameTimePeriod): use mainPeriodWeekIds since comparison uses same time period as primary
  // - YTD/Last Week + Prior Year: use full comparison period (full year) so the comparison line shows the complete year
  // - Other scenarios: use comparisonWeekIdsForMainPeriod for 1:1 alignment with primary
  // Missing weeks are padded with 0 (product decision) so the chart has the correct number of categories.
  const comparisonData = useMemo(() => {
    // Skip alignment for forecasts or when explicitly disabled
    if (skipDataAlignment) return rawComparisonData;
    // For compare mode (comparing different metrics/groups), comparison uses same time period as primary
    if (isComparisonSameTimePeriod) {
      if (!mainPeriodWeekIds.length) return rawComparisonData;
      return alignDataToWeekIds(rawComparisonData, mainPeriodWeekIds, alignmentOptions);
    }
    if (shouldSpanFullYear) {
      // For YTD/Last Week + Prior Year, align to the full comparison year
      if (!fullComparisonPeriodWeekIds.length) return rawComparisonData;
      return alignDataToWeekIds(rawComparisonData, fullComparisonPeriodWeekIds, alignmentOptions);
    }
    // For regular comparisons, align to the weeks corresponding to main period
    if (!comparisonWeekIdsForMainPeriod.length) return rawComparisonData;
    return alignDataToWeekIds(rawComparisonData, comparisonWeekIdsForMainPeriod, alignmentOptions);
  }, [rawComparisonData, isComparisonSameTimePeriod, mainPeriodWeekIds, shouldSpanFullYear, fullComparisonPeriodWeekIds, comparisonWeekIdsForMainPeriod, alignmentOptions, skipDataAlignment]);

  // Calculate totals using the utility function
  // For market share mode, compute overall ratio: sum(brand) / sum(market)
  const primaryTotal = useMemo(() => {
    if (marketShareBuilder && marketMainRows.length > 0) {
      return computeMarketShareTotal(mainRows, marketMainRows, indexName, fieldName, entityType);
    }
    return calculateMetricTotal(primaryData, field, {
      isLastWeekWithPriorYear,
      aggregationFunctionOverride: aggregationFunctionForTotal
    });
  }, [primaryData, field, isLastWeekWithPriorYear, aggregationFunctionForTotal, marketShareBuilder, mainRows, marketMainRows, indexName, fieldName, entityType]);

  const comparisonTotal = useMemo(() => {
    if (marketShareBuilder && marketComparisonRows.length > 0) {
      return computeMarketShareTotal(comparisonRows, marketComparisonRows, comparisonIndexName || indexName, comparisonFieldName || fieldName, entityType);
    }
    return calculateComparisonTotal(comparisonData, field, {
      isLastWeekWithPriorYear,
      aggregationFunctionOverride: aggregationFunctionForTotal,
      comparisonWeekIdsToInclude: isComparisonSameTimePeriod ? mainPeriodWeekIds : comparisonWeekIdsForMainPeriod
    });
  }, [comparisonData, field, isLastWeekWithPriorYear, aggregationFunctionForTotal, isComparisonSameTimePeriod, mainPeriodWeekIds, comparisonWeekIdsForMainPeriod, marketShareBuilder, comparisonRows, marketComparisonRows, comparisonIndexName, indexName, comparisonFieldName, fieldName, entityType]);

  const percentChange = useMemo(
    () => calculatePercentChange(primaryTotal, comparisonTotal),
    [primaryTotal, comparisonTotal]
  );

  // Last values
  const lastPrimaryValue = primaryData.length > 0 ? primaryData[primaryData.length - 1].value : null;
  const lastComparisonValue = comparisonData.length > 0 ? comparisonData[comparisonData.length - 1].value : null;
  const lastValuePercentChange =
    lastPrimaryValue !== null && lastComparisonValue !== null
      ? calculatePercentChange(lastPrimaryValue, lastComparisonValue)
      : undefined;

  // Build series for SplineChartV2
  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-primary`,
      name: field.displayName || fieldName,
      data: primaryData
    }),
    [indexName, fieldName, field.displayName, primaryData]
  );

  const comparisonSeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-comparison`,
      name: `${field.displayName || fieldName} (Comparison)`,
      data: comparisonData
    }),
    [indexName, fieldName, field.displayName, comparisonData]
  );

  const metricType = metricTypeOverride || field.metricType;

  // ============ Formatted Values ============
  const formatOptions = {
    decimalPlaces: 2,
    showFullValue: metricType === METRICTYPE.PERCENT,
    showNegative: true
  };

  const formattedPrimaryTotal = useMemo(
    () => formatMetric(primaryTotal, metricType, formatOptions),
    [formatMetric, primaryTotal, metricType]
  );

  const formattedComparisonTotal = useMemo(
    () => formatMetric(comparisonTotal, metricType, formatOptions),
    [formatMetric, comparisonTotal, metricType]
  );

  const formattedPercentChange = useMemo(
    () =>
      percentChange !== undefined
        ? formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
        : '',
    [formatMetric, percentChange]
  );

  const formattedLastPrimaryValue = useMemo(
    () => (lastPrimaryValue !== null ? formatMetric(lastPrimaryValue, metricType, formatOptions) : ''),
    [formatMetric, lastPrimaryValue, metricType]
  );

  const formattedLastComparisonValue = useMemo(
    () => (lastComparisonValue !== null ? formatMetric(lastComparisonValue, metricType, formatOptions) : ''),
    [formatMetric, lastComparisonValue, metricType]
  );

  const formattedLastValuePercentChange = useMemo(
    () =>
      lastValuePercentChange !== undefined
        ? formatMetric(Math.abs(lastValuePercentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
        : '',
    [formatMetric, lastValuePercentChange]
  );

  return {
    // Series data
    primarySeries,
    comparisonSeries,

    // Raw data points
    primaryData,
    comparisonData,

    // Loading state
    isLoading,

    // Field info
    metricType,
    field,

    // Raw numeric values
    primaryTotal,
    comparisonTotal,
    percentChange,
    lastPrimaryValue,
    lastComparisonValue,
    lastValuePercentChange,

    // Formatted values
    formattedPrimaryTotal,
    formattedComparisonTotal,
    formattedPercentChange,
    formattedLastPrimaryValue,
    formattedLastComparisonValue,
    formattedLastValuePercentChange,

    // Time period info
    isYTDWithPriorYear,
    isLastWeekWithPriorYear,
    primaryStartWeekId: startWeekId,
    primaryEndWeekId: endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,

    // Raw response for custom calculations
    response
  };
};

export default useMetricByWeekIdV2;
